import { useContext, useState } from "react";
import { Link } from "react-router";

import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { ClientContent, ClientFileInfoLineContext } from "@shared";

import { CaseNoteResponse, ClientDetailResponse } from "@/api";
import { clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { CaseNoteContent } from "../case-note-content/CaseNoteContent";
import { CaseNoteEditDialog } from "../case-note-edit/CaseNoteEditDialog";
import { CaseNoteFromDocumentEditDialog } from "../case-note-from-document-edit/CaseNoteFromDocumentEditDialog";
import { useCaseNoteDelete } from "./hooks/useCaseNoteDelete";

interface Props {
  client: ClientDetailResponse;
  selectedCaseNote: CaseNoteResponse | null;
  onDeleted: () => void;
}

export function CaseNoteOverview({ client, selectedCaseNote, onDeleted }: Props) {
  const { t } = useTranslation();
  const infoLine = useContext(ClientFileInfoLineContext)?.();
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const { handleDelete, isDeleting } = useCaseNoteDelete(client, onDeleted);

  return (
    <ClientContent
      customActions={
        selectedCaseNote ? (
          <>
            {selectedCaseNote.canDelete ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteRoundedIcon />}
                disabled={isDeleting}
                onClick={() => handleDelete(selectedCaseNote)}
              >
                {t("common.delete")}
              </Button>
            ) : (
              <Box />
            )}
            <Stack direction="row" alignItems="center" gap={2}>
              {infoLine}
              {selectedCaseNote.canEdit && (
                <Button variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => setShowEditDialog(true)}>
                  {t("common.edit")}
                </Button>
              )}
            </Stack>
          </>
        ) : null
      }
    >
      {selectedCaseNote ? (
        <>
          <CaseNoteContent
            clientId={client.clientId}
            caseNote={selectedCaseNote}
            headerAction={
              <IconButton
                component={Link}
                to={clientPath.caseNotePopout({ id: client.clientId, noteId: selectedCaseNote.caseNoteId })}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("clients.client.caseNotes.openDetailedOverview")}
              >
                <LaunchRoundedIcon />
              </IconButton>
            }
          />
          {showEditDialog &&
            (selectedCaseNote.attachedDocument ? (
              <CaseNoteFromDocumentEditDialog
                caseNote={selectedCaseNote}
                client={client}
                onClose={() => setShowEditDialog(false)}
              />
            ) : (
              <CaseNoteEditDialog
                caseNote={selectedCaseNote}
                client={client}
                onClose={() => setShowEditDialog(false)}
              />
            ))}
        </>
      ) : (
        <Stack bgcolor={"white"} flex={1} p={2} gap={2}>
          <Typography>{t("clients.client.caseNotes.overviewPlaceholder")}</Typography>
        </Stack>
      )}
    </ClientContent>
  );
}
